import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Loader2, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { usePlanFeature } from "@/components/PlanGate";

const FIELDS = [
  ["problem", "Descrição do problema"], ["diagnosis", "Diagnóstico preliminar (sugestão)"], ["recommended_service", "Serviço recomendado"],
  ["client_description", "Descrição para o cliente"], ["observations", "Observações"], ["warranty", "Garantia sugerida"],
];

export const AiDescribeButton = ({ kind, equipment, seed, onApply, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [symptom, setSymptom] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [applied, setApplied] = useState({});
  const navigate = useNavigate();
  const { allowed } = usePlanFeature("ai");

  const run = async () => {
    const text = (symptom || seed || "").trim();
    if (!text) { toast.error("Escreva o que o cliente relatou ou o que você observou"); return; }
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/ai/describe", { kind, equipment: equipment?.name, brand: equipment?.brand, model: equipment?.model, symptom: text });
      setRes(data); setApplied({});
    } catch (e) { toast.error(e?.response?.data?.detail || "A IA não respondeu. Tente de novo."); } finally { setBusy(false); }
  };
  const apply = (k) => { onApply(k, res[k]); setApplied({ ...applied, [k]: true }); toast.success("Sugestão aplicada"); };
  const applyAll = () => { FIELDS.forEach(([k]) => res[k] && onApply(k, res[k])); setOpen(false); toast.success("Sugestões aplicadas — revise e ajuste se precisar"); };

  return (
    <>
      <Button type="button" variant="outline" onClick={() => { if (!allowed) { toast.info("Assistente IA disponível a partir do plano Profissional", { action: { label: "Ver planos", onClick: () => navigate("/plans") } }); return; } setSymptom(seed || ""); setOpen(true); }} className={`h-11 border-primary/40 text-primary hover:bg-primary/5 ${className}`} data-testid="ai-describe-btn">
        {allowed ? <Sparkles className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />} Gerar descrição com IA{!allowed && <span className="ml-1 text-[10px] font-bold uppercase text-amber-600">Pro</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="ai-describe-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Assistente de descrição</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Escreva do seu jeito. A IA transforma em texto profissional — tudo é <b>sugestão</b>: confirme antes de usar.</p>
          <Textarea value={symptom} onChange={(e) => setSymptom(e.target.value)} placeholder='Ex.: "Geladeira Samsung não está congelando e compressor liga e desliga"' className="min-h-[90px] text-base" data-testid="ai-symptom-input" />
          <Button onClick={run} disabled={busy} className="h-12" data-testid="ai-generate-btn">{busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando...</> : <><Sparkles className="h-4 w-4 mr-2" /> Gerar sugestões</>}</Button>
          {res && (
            <div className="space-y-3" data-testid="ai-results">
              {FIELDS.filter(([k]) => res[k]).map(([k, label]) => (
                <div key={k} className="rounded-xl border border-border p-3" data-testid={`ai-field-${k}`}>
                  <div className="flex items-center justify-between mb-1"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
                    <Button size="sm" variant={applied[k] ? "secondary" : "outline"} className="h-8" onClick={() => apply(k)} data-testid={`ai-apply-${k}`}>{applied[k] ? <><Check className="h-3.5 w-3.5 mr-1" /> Usado</> : "Usar"}</Button></div>
                  <p className="text-sm whitespace-pre-wrap">{res[k]}</p>
                </div>
              ))}
              {Array.isArray(res.tests) && res.tests.length > 0 && (
                <div className="rounded-xl border border-dashed border-border p-3"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Testes sugeridos para investigação</p>
                  <ul className="text-sm list-disc pl-4 space-y-0.5">{res.tests.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
              )}
              <Button className="w-full h-12" onClick={applyAll} data-testid="ai-apply-all"><Check className="h-4 w-4 mr-1" /> Usar todas as sugestões</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
